import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, StateToken, Store } from "@ngxs/store";
import { EntraIdResourcesService } from "./entra-id-resources.service";
import {
    ClearEntraIdResource,
    LoadAllEntraIdResources,
    LoadDataTableEntraIdResources,
    SetEntraIdResource,
    UpdateCountersEntraIdResources,
    LoadAllEntraIdCharts,
    LoadEntraIdChartsFilteredData
} from "./entra-id-resources.actions";
import { finalize, forkJoin, tap } from "rxjs";
import { EntraIdResourcesStateModel, EntraIdResource, EntraIdCounterChartsData } from "./entra-id-resources.model";
import { PreferencesFiltersState } from "src/app/store/preferences/filters/preferences-filters.state";
import { CompliantRatioService } from "src/app/shared/service/compliant-ratio.service";
import { EntraIdResourceTypeEnum } from "src/app/shared/enum/entra-id-resource-type.enum";
import { ChartComplianceRatioItem } from "src/app/shared/component/chart-compliance-ratio/chart-compliance-ratio.component";
import { ChartComplianceTotalItem } from "src/app/shared/component/chart-compliance-total/chart-compliance-total.component";
import { ChartVersusItem } from "src/app/shared/component/chart-versus/chart-versus.component";
import { DateService } from "src/app/shared/service/date.service";
import { EntraIdResourceTypeLabelPipe } from "src/app/shared/pipe/entra-id-resource-type-label.pipe";
import { ColorEnum } from "src/app/shared/enum/color.enum";
import { ItemTypeEnum } from "src/app/shared/enum/item-type.enum";
import { MetaRequestHelper } from "src/app/store/meta/meta-request.helper";
import { MetaService } from "src/app/store/meta/meta.service";
import { EntraIdConfig } from "src/app/store/preferences/providers/preferences-providers.model";
import { PreferencesProvidersState } from "src/app/store/preferences/providers/preferences-providers.state";
import { PreferencesChartsState } from "src/app/store/preferences/charts/preferences-charts.state";
import { any } from "@amcharts/amcharts4/.internal/core/utils/Array";
import { LoadAzureChartsFilteredData } from "../azure/azure-resources.actions";

export const ENTRAID_RESOURCES_TOKEN: StateToken<EntraIdResourcesStateModel> = new StateToken('entraIdResources');

const EntraIdResourcesStateModelDefaults: EntraIdResourcesStateModel = {
    resourcesPerTenant: {},
    compliantRatio: '0%',
    totalItems: '0',
    compliantItems: '0',
    notCompliantItems: '0',
    datatable: [],
    selectedResource: {
        account: '',
        age: 0,
        id: '',
        tfstates: [],
        repositories: [],
        issues: [],
        name: '',
        source: '',
        state: 'ignored',
        type: ''
    },
    chartsDataPerTenant: {},
    chartData: {
        filteredComplianceRatioChartData: {
            values: []
        },
        filteredComplianceTotalChartData: {
            values: []
        },
        filteredVersusChartData: {
            values: []
        }
    },
}

@State<EntraIdResourcesStateModel>({
    name: ENTRAID_RESOURCES_TOKEN,
    defaults: EntraIdResourcesStateModelDefaults
})

@Injectable()
export class EntraIdResourcesState {

    private readonly KEY = ENTRAID_RESOURCES_TOKEN.getName()

    constructor(
        private entraIdResourcesService: EntraIdResourcesService,
        private store: Store,
        private dateService: DateService,
        private entraIdResourceTypeLabelPipe: EntraIdResourceTypeLabelPipe,
        private metaService: MetaService
    ) {
    }

    //#region Selectors
    @Selector([ENTRAID_RESOURCES_TOKEN])
    static getResources(state: EntraIdResourcesStateModel) {
        return (tenant:string) => state.resourcesPerTenant[tenant]
    }

    @Selector([ENTRAID_RESOURCES_TOKEN])
    static getCompliantRatio(state: EntraIdResourcesStateModel) {
        return state.compliantRatio
    }

    @Selector([ENTRAID_RESOURCES_TOKEN])
    static getTotalItems(state: EntraIdResourcesStateModel) {
        return state.totalItems
    }

    @Selector([ENTRAID_RESOURCES_TOKEN])
    static getCompliantItems(state: EntraIdResourcesStateModel) {
        return state.compliantItems
    }

    @Selector([ENTRAID_RESOURCES_TOKEN])
    static getNotCompliantItems(state: EntraIdResourcesStateModel) {
        return state.notCompliantItems
    }

    @Selector([ENTRAID_RESOURCES_TOKEN])
    static getDatatable(state: EntraIdResourcesStateModel) {
        return state.datatable
    }

    @Selector([ENTRAID_RESOURCES_TOKEN])
    static getSelectedResource(state: EntraIdResourcesStateModel) {
        return state.selectedResource
    }

    @Selector([ENTRAID_RESOURCES_TOKEN])
    static getFilteredComplianceRatioChartData(state: EntraIdResourcesStateModel) {
        return state.chartData.filteredComplianceRatioChartData
    }

    @Selector([ENTRAID_RESOURCES_TOKEN])
    static getFilteredComplianceTotalChartData(state: EntraIdResourcesStateModel) {
        return state.chartData.filteredComplianceTotalChartData
    }

    @Selector([ENTRAID_RESOURCES_TOKEN])
    static getFilteredVersusChartData(state: EntraIdResourcesStateModel) {
        return state.chartData.filteredVersusChartData
    }
    //#endregion Selectors

    @Action(LoadAllEntraIdResources)
    loadAllEntraIdResources(ctx: StateContext<EntraIdResourcesStateModel>) {
        const entraIdConfig = this.store.selectSnapshot(PreferencesProvidersState.getEntraIdConfig);
        const tenantKeys = entraIdConfig.preferences && entraIdConfig.preferences.length > 0
            ? entraIdConfig.preferences
            : entraIdConfig.tenants.map((t: any) => t.tenant_key);

        if (tenantKeys.length === 0) return;

        const staleTenantKeys = tenantKeys.filter((id: string) => this.metaService.isStale(this.KEY + "-tenant-" + id));

        if (staleTenantKeys.length === 0) return ctx.dispatch(new LoadDataTableEntraIdResources());

        return forkJoin(
            staleTenantKeys.map((tenantKey: string) =>
                MetaRequestHelper.wrapRequest(
                    this.store,
                    this.KEY + "-tenant-" + tenantKey,
                    this.entraIdResourcesService.loadEntraIdResources(tenantKey),
                    resources => {
                        ctx.patchState({
                            resourcesPerTenant: {
                                ...ctx.getState().resourcesPerTenant,
                                [tenantKey]: resources
                            }
                        })
                    }
                )
            )
        ).pipe(
            finalize(() => ctx.dispatch(new LoadDataTableEntraIdResources()))
        );
    }

    @Action(LoadDataTableEntraIdResources)
    loadDataTableEntraIdResources(ctx: StateContext<EntraIdResourcesStateModel>, {}: LoadDataTableEntraIdResources) {
        const state = ctx.getState()
        let datatable_resource: EntraIdResource[] = []

        for (const [key, resources] of Object.entries(state.resourcesPerTenant)) {
            resources.forEach((resource) => {
                datatable_resource.push(resource)
            })
        }

        let filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.ENTRA_ID_RESOURCES]]
        filters.forEach(filter => {
            if (filter.key == 'issuesCount') {
                datatable_resource = datatable_resource.filter((resource) => resource['issues'].length.toString() === filter.value)
            } else if (filter.key == 'issuesType') {
                datatable_resource = datatable_resource.filter((resource) => {
                    let issuesTypeFound = false
                    resource.issues.forEach(issue => {
                        issuesTypeFound = issuesTypeFound || filter.value.includes(issue.issue_type)
                    })
                    return issuesTypeFound
                })
            } else if (filter.key == 'age') {
                let unknownAge = 99999
                let filterAge: number = unknownAge
                if (filter.value == 'today') {
                    filterAge = 0
                } else if (filter.value.startsWith('last_')) {
                    filterAge = filter.value.substring(5, 7)
                }
                datatable_resource = datatable_resource.filter((resource) => {
                    let ageInRange = false
                    if (filterAge == unknownAge) {
                        ageInRange = resource.age == filterAge
                    } else {
                        ageInRange = resource.age <= filterAge
                    }
                    return ageInRange
                })
            } else {
                if (Array.isArray(filter.value)) {
                    datatable_resource = datatable_resource.filter((resource) => filter.value.includes(resource[filter.key as keyof EntraIdResource]))
                } else {
                    datatable_resource = datatable_resource.filter((resource) => resource[filter.key as keyof EntraIdResource] === filter.value)
                }
            }
        });
        ctx.patchState({
            datatable: datatable_resource
        })
        ctx.dispatch(new UpdateCountersEntraIdResources())
    }

    @Action(UpdateCountersEntraIdResources)
    updateCountersEntraIdResources(ctx: StateContext<EntraIdResourcesStateModel>) {
        const state = ctx.getState()
        let totalItemsCounter = 0
        let compliantItemsCounter = 0
        let notCompliantItemsCounter = 0
        state.datatable.forEach(resource => {
            if (resource.state == 'compliant') {
                totalItemsCounter++;
                compliantItemsCounter++;
            } else if (resource.state == 'not_compliant') {
                totalItemsCounter++;
                notCompliantItemsCounter++;
            }
        });
        ctx.patchState({
            compliantRatio: CompliantRatioService.calculateCompliantRatio(compliantItemsCounter, notCompliantItemsCounter),
            totalItems: '' + totalItemsCounter,
            compliantItems: '' + compliantItemsCounter,
            notCompliantItems: '' + notCompliantItemsCounter
        })
    }

    @Action(ClearEntraIdResource)
    clearEntraIdResource(ctx: StateContext<EntraIdResourcesStateModel>) {
        return ctx.patchState({
            selectedResource: EntraIdResourcesStateModelDefaults.selectedResource
        })
    }

    @Action(SetEntraIdResource)
    setEntraIdResource(ctx: StateContext<EntraIdResourcesStateModel>, {resource_id, tenant}: SetEntraIdResource) {
        return this.entraIdResourcesService.loadEntraIdResourceDetails(tenant, resource_id)
            .pipe(
                tap((value: any) => {
                    return ctx.patchState({
                        selectedResource: value
                    })
                }),
            )
    }
    
    @Action(LoadAllEntraIdCharts)
    loadAllEntraIdCharts(ctx: StateContext<EntraIdResourcesStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig);
        const { start_date, end_date } = DateService.getChartsMeasuresDateRange(chartsConfig);
        let entraIdConfig: EntraIdConfig = this.store.selectSnapshot(PreferencesProvidersState.getEntraIdConfig)
        let wrapRequestList = []
        let tenants = entraIdConfig.preferences?.length
            ? entraIdConfig.tenants.filter(s => entraIdConfig.preferences!.includes(s.tenant_key))
            : entraIdConfig.tenants
        for (let entraId_tenant of tenants) {
            const ENTRA_ID_TENANT_CHART_KEY = this.KEY + "-charts-tenant-" + entraId_tenant.tenant_key + "-" + start_date + "-" + end_date + "-" + chartsConfig.granularity

            // In case the tenant has already been requested, skip the API call
            if (!this.metaService.isStale(ENTRA_ID_TENANT_CHART_KEY)) continue

            wrapRequestList.push(
                MetaRequestHelper.wrapRequest(
                    this.store,
                    ENTRA_ID_TENANT_CHART_KEY,
                    this.entraIdResourcesService.loadEntraIdMeasures(entraId_tenant.tenant_key, start_date, end_date, chartsConfig.granularity),
                    charts_payload => {
                        ctx.patchState({
                            chartsDataPerTenant: {...ctx.getState().chartsDataPerTenant, [entraId_tenant.tenant_key]: charts_payload.measures}
                        })
                    }
                )
            )
        }
        return forkJoin(wrapRequestList).pipe(
            finalize(
                () => ctx.dispatch(new LoadEntraIdChartsFilteredData())
            )
        )
    }

    mergeCountersByDate(input: { [tenant: string]: EntraIdCounterChartsData }): EntraIdCounterChartsData {
        const result: EntraIdCounterChartsData = {};

        for (const tenantKey of Object.keys(input)) {
            const dates = input[tenantKey];

            for (const dateKey of Object.keys(dates)) {
                const types = dates[dateKey];

                if (!result[dateKey]) {
                    result[dateKey] = {};
                }

                for (const typeKey of Object.keys(types)) {
                    if (!result[dateKey][typeKey]) {
                        result[dateKey][typeKey] = {
                            checked: 0,
                            unchecked: 0,
                            ignored: 0
                        };
                    }

                    result[dateKey][typeKey].checked += types[typeKey].checked;
                    result[dateKey][typeKey].unchecked += types[typeKey].unchecked;
                    result[dateKey][typeKey].ignored += types[typeKey].ignored;
                }
            }
        }

        return result;
    }

    @Action(LoadEntraIdChartsFilteredData)
    loadEntraIdChartsFilteredData(ctx: StateContext<EntraIdResourcesStateModel>) {
        const state = ctx.getState()

        let complianceRatioChartValues: ChartComplianceRatioItem[] = [];
        let complianceTotalChartValues: ChartComplianceTotalItem[] = [];
        let complianceVersusChartValues: ChartVersusItem[] = [];
        let filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.ENTRA_ID_MEASURES]]
        let resource_types: string[] = []
        let tenant_keys: string[] = []
        filters.forEach(filter => {
            if (filter.key === 'type') {
                resource_types = filter.value
            } else if (filter.key === 'tenant') {
                tenant_keys = filter.value
            }
        })
        if (resource_types.length == 0) {
            resource_types = Object.keys(EntraIdResourceTypeEnum)
        }

        let countersChartData: EntraIdCounterChartsData = {}

        // Filter chartsDataPerTenant by tenant filter or use all tenants
        let filteredChartsDataPerTenant: Record<string, EntraIdCounterChartsData> = {}
        if (tenant_keys.length > 0) {
            for (const tenant_key of tenant_keys) {
                if (state.chartsDataPerTenant[tenant_key]) {
                    filteredChartsDataPerTenant[tenant_key] = state.chartsDataPerTenant[tenant_key]
                }
            }
        } else {
            filteredChartsDataPerTenant = state.chartsDataPerTenant
        }
        countersChartData = this.mergeCountersByDate(filteredChartsDataPerTenant)

        for (const [dateString, charts_data] of Object.entries(countersChartData)) {
            let formattedDate = this.dateService.formatLongDate(dateString)
            let compliantCount = 0
            let notCompliantCount = 0
            for (const type in charts_data) {
                if (resource_types.includes(type)) {
                    const typeMeasures = charts_data[type];
                    compliantCount += typeMeasures.checked;
                    notCompliantCount += typeMeasures.unchecked;
                }
            }
            complianceRatioChartValues.push({
                "dateString": formattedDate,
                "date": new Date(formattedDate),
                "value": CompliantRatioService.calculateCompliantRatio(compliantCount, notCompliantCount)
            });
            complianceTotalChartValues.push({
                "dateString": formattedDate,
                "date": new Date(formattedDate),
                "compliantCount": compliantCount,
                "notCompliantCount": notCompliantCount,
                "total": compliantCount + notCompliantCount,
                "delta": Math.abs(compliantCount - notCompliantCount)
            });
        }
        let lastDate = Object.keys(countersChartData)[Object.keys(countersChartData).length - 1]
        let lastMeasure = countersChartData[lastDate]
        for (const type in lastMeasure) {
            if (resource_types.includes(type)) {
                let typeMeasure = lastMeasure[type]
                let colorKey = 'charts_color_' + complianceVersusChartValues.length
                let dateData = {
                    "type": this.entraIdResourceTypeLabelPipe.transform(type),
                    "count": typeMeasure.checked + typeMeasure.unchecked + typeMeasure.ignored,
                    "color": ColorEnum[colorKey as keyof typeof ColorEnum]
                };
                complianceVersusChartValues.push(dateData);
            }
        }
        return ctx.patchState({
            chartData: {
                filteredComplianceRatioChartData: {
                    values: complianceRatioChartValues
                },
                filteredComplianceTotalChartData: {
                    values: complianceTotalChartValues
                },
                filteredVersusChartData: {
                    values: complianceVersusChartValues
                }
            }
        })
    }
}
